import { useEffect, forwardRef, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  DisconnectReason,
  Participant,
  RemoteParticipant,
  RemoteTrackPublication,
  RoomEvent,
  Track,
} from 'livekit-client';
import { useRoomContext, useTracks } from '@livekit/components-react';
import { useVideoState, VideoEvents } from '@msi-alta/opvideo-state-machine';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { getOpParams } from 'utils/getOpParams';
import { setAlert } from 'routes/AppContainer/actions';
import {
  connectionStates,
  callStates,
} from 'containers/OpVideoPlayer/constants';
import { timeoutStates, disconnectReasonStatus } from '../constants';
import { useDisconnectCallTimeout } from '../hooks/useDisconnectCallTimeout';

// TODO: Update opvideo-state-machine so that states can be imported when isolatedModules is enabled
const VideoStates = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  PREVIEW: 'PREVIEW',
  SURVEILLANCE: 'SURVEILLANCE',
  CALL: 'CALL',
  FAILED: 'FAILED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',
};

// todo: reference this from other file instead of copy-pasting
interface LiveKitVideoPlayerProps {
  callState?: string;
  connectionState?: string;
  isMicrophoneEnabled: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setCallState?: (state: string) => void;
  setConnectionState?: (state: string) => void;
  setErrorProps: (props: object | null) => void;
}

interface LiveKitVideoPlayerStageProps extends LiveKitVideoPlayerProps {
  setShouldStartLive: (state: boolean) => void;
}

export interface TimeoutState {
  previewTimeout: ReturnType<typeof setTimeout> | null;
  callTimeout: ReturnType<typeof setTimeout> | null;
  disconnectIfNoInteractionTimeout: ReturnType<typeof setTimeout> | null;
  type: string;
}

interface Metadata {
  isOpvideoDevice?: boolean;
  isIntercom?: boolean;
  isUser?: boolean;
}

export const LiveKitCallManager = forwardRef<
  HTMLVideoElement,
  LiveKitVideoPlayerStageProps
>(
  (
    {
      connectionState = '',
      callState = '',
      isMicrophoneEnabled,
      setCallState = () => {},
      setConnectionState = () => {},
      setIsLoading,
      setShouldStartLive,
      setErrorProps,
    },
    ref,
  ) => {
    const currentTracks = useRef<string[]>([]);
    const roomContext = useRoomContext();
    const tracksTracker = useTracks(
      [Track.Source.Camera, Track.Source.Microphone],
      {
        updateOnlyOn: [
          RoomEvent.TrackPublished,
          RoomEvent.TrackSubscribed,
          RoomEvent.TrackSubscriptionPermissionChanged,
          RoomEvent.TrackSubscriptionStatusChanged,
        ],
      },
    );

    const participantsUnmuted = useRef<string[]>([]);
    const [monitoringParticipantMicTracks, setMonitoringParticipantMicTracks] =
      useState(false);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const timeoutState = useRef<TimeoutState>({
      callTimeout: null,
      previewTimeout: null,
      disconnectIfNoInteractionTimeout: null,
      type: timeoutStates.NO_TIMEOUT,
    });
    const [shouldResetTimeout, setShouldResetTimeout] =
      useState<boolean>(false);

    const { orgId } = getOpParams();
    const { value: isVideoStateMachineEnabled } = useFeatureFlag(
      'IS_VIDEO_STATE_MACHINE_ENABLED',
      orgId,
    );

    const { videoState, sendEvent } = useVideoState();
    const { value: currentVideoState } = videoState;

    /* 
      no-await-in-loop rule was disabled since we need to await the result of each attempt 
      before proceeding to the next iteration of the loop.
    */
    const enableMicrophone = useCallback(
      async (newState: boolean) => {
        let retries = 0;
        let err = null;
        const maxRetries = 3;
        while (retries < maxRetries) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await roomContext?.localParticipant?.setMicrophoneEnabled(newState);

            if (isVideoStateMachineEnabled) {
              sendEvent({
                type: newState
                  ? VideoEvents.MicEnabled
                  : VideoEvents.MicDisabled,
              });
            }
            return;
          } catch (error) {
            err = error;
            retries++;
            if (retries < maxRetries) {
              console.error(
                `Failed setting microphone state on attempt ${retries}. Error details:` +
                  error,
              );
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => {
                setTimeout(resolve, 200);
              });
            }
          }
        }
        if (isVideoStateMachineEnabled) {
          sendEvent({ type: VideoEvents.MicDisabled });
        }

        dispatch(setAlert('error', t(`Failed to enable microphone: ${err}`)));
        console.error(
          `Maximum retries on setMicrophoneEnabled exceeded. Unable to set microphone state: ${err}`,
        );
      },
      [
        isVideoStateMachineEnabled,
        dispatch,
        t,
        roomContext?.localParticipant,
        sendEvent,
      ],
    );

    const manageAnswerOrMic = useCallback(() => {
      /*
        Enable/disable mic whenever the mic toggle state changes and the session is in call state
        On Preview Mode, the mic should always be turned off
      */
      if (isVideoStateMachineEnabled) {
        enableMicrophone(
          currentVideoState === VideoStates.CALL && isMicrophoneEnabled,
        );
      } else if (connectionState === connectionStates.CONNECTED) {
        enableMicrophone(callState === callStates.CALL && isMicrophoneEnabled);
      }
    }, [
      connectionState,
      isVideoStateMachineEnabled,
      currentVideoState,
      enableMicrophone,
      callState,
      isMicrophoneEnabled,
    ]);

    useEffect(() => {
      manageAnswerOrMic();
    }, [manageAnswerOrMic, callState, isMicrophoneEnabled]);

    const cleanUpTracks = useCallback(() => {
      if (
        tracksTracker?.length === 0 &&
        ref !== null &&
        typeof ref !== 'function'
      ) {
        // Clean up if there were no tracks before
        const currentStream = ref?.current?.srcObject;
        if (currentStream instanceof MediaStream) {
          const previousTracks = currentStream.getTracks();
          previousTracks?.forEach((track) => {
            currentStream.removeTrack(track);
          });
        }
      }
    }, [ref, tracksTracker?.length]);

    const updateTracks = useCallback(() => {
      if (
        tracksTracker?.length > 0 &&
        ref !== null &&
        typeof ref !== 'function'
      ) {
        let shouldUpdate = false;
        let stream = ref?.current?.srcObject;
        if (!stream) {
          stream = new MediaStream();
        }
        tracksTracker?.forEach((track) => {
          if (
            !track.participant.isLocal &&
            track.publication.track?.mediaStreamTrack &&
            stream !== null &&
            stream !== undefined
          ) {
            if (
              !currentTracks.current.includes(track.publication.trackSid) &&
              stream instanceof MediaStream
            ) {
              stream.addTrack(track.publication.track?.mediaStreamTrack);
              currentTracks.current.push(track.publication.trackSid);
              shouldUpdate = true;
              if (isVideoStateMachineEnabled) {
                sendEvent({ type: VideoEvents.ConnectionSuccessful });
                sendEvent({ type: VideoEvents.WebRTCReconnected });
              } else if (connectionState !== connectionStates.CONNECTED) {
                // Connection state should be connected only once we add tracks to the video element
                setConnectionState(connectionStates.CONNECTED);
              }
            }
          }
        });
        if (
          shouldUpdate &&
          ref != null &&
          typeof ref !== 'function' &&
          stream instanceof MediaStream &&
          ref.current !== null
        ) {
          // eslint-disable-next-line no-param-reassign
          ref.current.srcObject = stream;
        }
      }
    }, [
      connectionState,
      isVideoStateMachineEnabled,
      ref,
      sendEvent,
      setConnectionState,
      tracksTracker,
    ]);

    useEffect(() => {
      cleanUpTracks();
      updateTracks();
    }, [cleanUpTracks, updateTracks]);

    const startTeardown = useCallback(async () => {
      if (roomContext) {
        if (isVideoStateMachineEnabled) {
          sendEvent({ type: VideoEvents.EndCall });
          await roomContext?.disconnect();
          sendEvent({ type: VideoEvents.TeardownCompleted });
        } else {
          setConnectionState(connectionStates.DISCONNECTED);
          await roomContext?.disconnect();
        }
      }
    }, [
      isVideoStateMachineEnabled,
      sendEvent,
      roomContext,
      setConnectionState,
    ]);

    const getUnmutedParticipants = useCallback(() => {
      if (roomContext && !monitoringParticipantMicTracks) {
        setMonitoringParticipantMicTracks(true);

        participantsUnmuted.current.push(
          roomContext.localParticipant.name + '',
        );

        roomContext.on('trackUnmuted', (_, participant: Participant) => {
          participantsUnmuted.current.push('' + participant.name);
        });

        roomContext.on('trackMuted', (_, participant: Participant) => {
          participantsUnmuted.current = participantsUnmuted.current.filter(
            (p) => p !== participant.name,
          );
        });

        roomContext.on(
          'trackPublished',
          (track: RemoteTrackPublication, participant: RemoteParticipant) => {
            let metadata: Metadata = {};
            try {
              metadata = JSON.parse(participant.metadata || '{}');
            } catch {
              // do nothing
            }

            if (metadata?.isOpvideoDevice) {
              participantsUnmuted.current.push('' + participant.name);
            }
          },
        );
      }
    }, [monitoringParticipantMicTracks, roomContext]);

    useEffect(() => {
      getUnmutedParticipants();
    }, [getUnmutedParticipants]);

    useEffect(() => {
      roomContext?.on('reconnecting', () => {
        if (isVideoStateMachineEnabled) {
          sendEvent({ type: VideoEvents.ShouldReconnect });
        }
      });
      roomContext?.on('reconnected', () => {
        if (isVideoStateMachineEnabled) {
          sendEvent({ type: VideoEvents.WebRTCReconnected });
        }
      });
      roomContext?.on(
        'disconnected',
        (reason: DisconnectReason | undefined) => {
          if (isVideoStateMachineEnabled) {
            if (
              reason !== disconnectReasonStatus.CLIENT_INITIATED &&
              reason !== disconnectReasonStatus.PARTICIPANT_REMOVED &&
              reason !== disconnectReasonStatus.SIGNAL_CLOSE
            ) {
              sendEvent({ type: VideoEvents.ConnectionFailed });
            }
            sendEvent({ type: VideoEvents.StartTeardown });
            sendEvent({ type: VideoEvents.TeardownCompleted });
          }
        },
      );
    }, [isVideoStateMachineEnabled, roomContext, sendEvent]);

    const reconnectToPreview = useCallback(async () => {
      await startTeardown();
      setErrorProps(null);
      setIsLoading(true);
      setShouldStartLive(true);
      setShouldResetTimeout(true);
      if (isVideoStateMachineEnabled) {
        sendEvent({ type: VideoEvents.ConnectionStart });
      } else {
        setConnectionState(connectionStates.DISCONNECTED);
        setCallState(callStates.PREVIEW);
      }
    }, [
      startTeardown,
      setErrorProps,
      setIsLoading,
      setConnectionState,
      setCallState,
      setShouldStartLive,
      isVideoStateMachineEnabled,
      sendEvent,
    ]);

    useDisconnectCallTimeout(
      timeoutState,
      callState,
      shouldResetTimeout,
      connectionState,
      isVideoStateMachineEnabled,
      currentVideoState,
      startTeardown,
      setErrorProps,
      setIsLoading,
      setConnectionState,
      setShouldResetTimeout,
      reconnectToPreview,
    );

    // On unmount disconnect from room (if connected)
    useEffect(() => {
      return () => {
        startTeardown();
        setErrorProps(null);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setErrorProps]);

    return null;
  },
);
